<template>
  <div>
    <b-modal
      v-model="editorShown"
      :title="groupId ? 'Edit Group Session' : 'New Group Session'"
      @hidden="onHidden"
      @ok="submitGroup"
      centered
      scrollable
      size="lg"
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="
            w-100
            d-flex
            flex-row
            justify-content-between
            align-items-center
          "
        >
          <h5 class="modal-title">{{ formTitle }}</h5>
          <button
            type="button"
            aria-label="Close"
            class="close text-white"
            style="opacity: 1 !important"
            @click="$emit('onModify')"
          >
            ×
          </button>
        </div>
      </template>
      <template v-slot:modal-footer>
        <div v-show="errorMessage" class="text-left text-danger mt-1 mr-auto">
          {{ errorMessage }}
        </div>
        <div class="text-right mt-1 mr-2">
          <b-button @click="$emit('onModify')" variant="secondary">
            Cancel
          </b-button>
          <b-button
            v-if="!belongsToRecurrence"
            class="ml-2"
            @click="submitGroup"
            variant="primary"
          >
            Save
          </b-button>
          <b-button
            v-if="belongsToRecurrence"
            class="ml-2"
            @click="e => submitGroup(e, 'ModifySingleEvent')"
            variant="primary"
          >
            Modify this event
          </b-button>
          <b-button
            v-if="belongsToRecurrence"
            class="ml-2"
            @click="e => submitGroup(e, 'ModifyEventAndRecurrence')"
            variant="primary"
            :disabled="eventIsInPast"
          >
            Modify this and following events
          </b-button>
          <b-button
            v-if="belongsToRecurrence"
            class="ml-2"
            @click="e => submitGroup(e, 'ModifyRecurrence')"
            variant="primary"
            :disabled="eventIsInPast"
          >
            Modify all events
          </b-button>
        </div>
      </template>
      <b-row>
        <b-col>
          <b-form-group
            :label="this.requireProviderInEvents ? 'Provider*' : 'Provider'"
            :state="formErrors.providerId.length == 0 ? null : false"
            :invalid-feedback="formErrors.providerId.join(', ')"
          >
            <v-select :options="users" v-model="user" />
          </b-form-group>
          <b-form-group
            label="Facilitator(s)"
            :state="formErrors.facilitators.length == 0 ? null : false"
            :invalid-feedback="formErrors.facilitators.join(', ')"
          >
            <multiple-select
              v-model="form.facilitators"
              :options="users"
              maxHeight="calc(100vh - 319px)"
            >
            </multiple-select>
          </b-form-group>
          <b-form-group
            label="Date, time and duration"
            :state="formErrors.dateTimeDuration.length == 0 ? null : false"
            :invalid-feedback="formErrors.dateTimeDuration.join(', ')"
          >
            <date-time-duration-picker
              v-model="form.dateTimeDuration"
              :dateDisabledFn="datesNonWorking"
            />
          </b-form-group>
          <b-form-group
            label="Place of service*"
            :state="formErrors.placeOfService.length == 0 ? null : false"
            :invalid-feedback="formErrors.placeOfService.join(', ')"
          >
            <div class="select-places-of-service">
              <div>
                <v-select
                  class=""
                  :options="placesOfServiceOptions"
                  v-model="form.placeOfService"
                  :clearable="false"
                />
              </div>
            </div>
          </b-form-group>
          <b-form-group label="Room">
            <div class="select_rooms">
              <div>
                <v-select :options="rooms" v-model="form.room" />
              </div>
            </div>
          </b-form-group>

          <b-form-group v-if="isZoomEnabled" class="mt-2">
            <div class="d-flex align-items-center">
              <vue-toggles
                @click="form.scheduleZoomMeeting = !form.scheduleZoomMeeting"
                :value="form.scheduleZoomMeeting"
                height="30"
                width="50"
                checkedBg="#34C759"
                uncheckedBg="#FF6161"
                class="mr-2"
                :disabled="!user"
              />
              <span class="mr-2">Zoom Meeting</span>
              <img :src="require('../../assets/zoom_icon.png')" width="30" />
            </div>
            <div v-if="!user" class="text-danger mt-2" style="font-size: 15px">
              Select Provider to attach a Zoom meeting.
            </div>
          </b-form-group>
          <b-form-group v-if="isTeamsEnabled" class="mt-2">
            <div class="d-flex align-items-center">
              <vue-toggles
                @click="form.scheduleTeamsMeeting = !form.scheduleTeamsMeeting"
                :value="form.scheduleTeamsMeeting"
                height="30"
                width="50"
                checkedBg="#34C759"
                uncheckedBg="#FF6161"
                class="mr-2"
              />
              <span class="mr-2">Teams Meeting</span>
              <img :src="require('../../assets/icons/teams.png')" width="30" />
            </div>
          </b-form-group>
          <b-form-group v-if="isGoogleMeetEnabled" class="mt-2">
            <div class="d-flex align-items-center">
              <vue-toggles
                  @click="form.scheduleGoogleMeetMeeting = !form.scheduleGoogleMeetMeeting"
                  :value="form.scheduleGoogleMeetMeeting"
                  height="30"
                  width="50"
                  checkedBg="#34C759"
                  uncheckedBg="#FF6161"
                  class="mr-2"
                  :disabled="!user"
              />
              <span class="mr-2">Google Meet Meeting</span>
              <img :src="require('../../assets/google_icon.png')" width="30" />
            </div>
            <div v-if="!user" class="text-danger mt-2" style="font-size: 15px">
              Select Provider to attach a Google Meet meeting.
            </div>
          </b-form-group>
          <b-form-group class="mt-2">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <vue-toggles
                  @click="form.isRecurrent = !form.isRecurrent"
                  :value="form.isRecurrent"
                  height="30"
                  width="50"
                  checkedBg="#34C759"
                  uncheckedBg="#FF6161"
                  :disabled="eventIsInPast"
                />
                <span class="ml-2">Make event recurrent</span>
              </div>
            </div>
          </b-form-group>
          <div
            v-if="eventIsInPast && !form.isRecurrent"
            class="text-danger"
            style="font-size: 15px"
          >
            Recurrence is only available on group sessions in the future.
          </div>
          <div v-show="form.isRecurrent">
            <div class="position-relative">
              <div
                class="position-absolute"
                style="
                  inset: -63px 0px -16px -7px;
                  background-color: #ccc;
                  opacity: 0.5;
                  z-index: 2;
                "
                v-if="eventIsInPast"
              ></div>
              <recurrence-control
                v-model="form.recurrence"
                :startDate="startDateTime"
                :formErrors="formErrors.recurrence"
                :maxOccurrences="300"
              />
            </div>
          </div>
        </b-col>
        <b-col>
          <b-form-group
            label="Services*"
            :state="formErrors.services.value < 0 ? true : false"
            :invalid-feedback="formErrors.services.join('. ')"
          >
            <div ref="servicesContainer">
              <multiple-select-unit-rate
                v-model="form.services"
                :options="services"
                :no-cached="this.noCached"
                style=""
                :hide-units="
                  !hasCurrentUserPermissionAccess(
                    'others',
                    'patientBillingInfo'
                  )
                "
                :hide-mods="
                  !hasCurrentUserPermissionAccess(
                    'others',
                    'patientBillingInfo'
                  )
                "
                :list-max-height="'300px'"
              />
            </div>
          </b-form-group>

          <b-form-group
            label="Group Type"
            :state="formErrors.topicId.length == 0 ? null : false"
            :invalid-feedback="formErrors.topicId.join(', ')"
          >
            <div
              :style="
                `height:calc(100vh - ${servicesContainerHeight + 307}px);`
              "
            >
              <group-session-type-picker
                :topicId="form.topicId"
                :details="form.details"
                @change-group-type="form.topicId = $event"
                @change-details="form.details = $event"
              />
            </div>
          </b-form-group>
        </b-col>
        <b-col>
          <div class="multiple-patients">
            <patient-batch-picker
              v-model="form.eocs"
              :locationId="locationId"
              @addPatients="addPatients"
            ></patient-batch-picker>
          </div>
          <b-form-group
            label="Participants"
            :state="formErrors.eocs.length == 0 ? null : false"
            :invalid-feedback="formErrors.eocs.join(', ')"
          >
            <multiple-select
              v-model="form.eocs"
              :options="eocs"
              maxHeight="calc(100vh - 319px)"
              :allowDuplicates="false"
              :onDemand="true"
              :disableCache="true"
              :disableFilter="true"
              :search="fetchEocsOptionsPicker"
              :sortFn="(a, b) => 1"
              @optionRemoved="removeDiagnosisCode"
            >
              <template #option="{ data }">
                <div class="d-flex align-items-center">
                  <b-badge
                    :variant="statusVariant(data.status)"
                    class="text-white mr-1"
                    >{{ statusText(data.status) }}
                  </b-badge>
                  <b-badge
                    v-if="data.levelOfCare"
                    :style="{ background: data.levelOfCare.color }"
                    class="mr-1"
                    >{{ data.levelOfCare?.name.substr(0, 3).toUpperCase() }}
                  </b-badge>
                  <span>{{ formatEocObj(data) }}</span>
                </div>
              </template>
              <template #selected-option="{ data }">
                <div class="d-flex align-items-center justify-content-between">
                  <div style="max-width: 355px">
                    <div>{{ formatEocObj(data) }}</div>
                    <a href="#" @click="editIcdCodes(data)">
                      <span
                        v-if="
                          !form.icdCodes[data.id] ||
                            form.icdCodes[data.id].length === 0
                        "
                        >Manage Diagnosis Codes</span
                      >
                      <span
                        v-else
                        v-html="
                          form.icdCodes[data.id]
                            .map(u => u.data.code)
                            .join(', ')
                        "
                      ></span>
                    </a>
                  </div>
                  <b-form-select
                    v-model="form.status[data.id]"
                    style="font-weight: bold; width: 131px; font-size: 14px"
                    :style="{
                      color: statusLabelColor(form.status[data.id]),
                      backgroundColor: statusBackgroundColor(
                        form.status[data.id]
                      )
                    }"
                  >
                    <b-form-select-option
                      v-for="status in statuses"
                      :key="status.value"
                      :value="status.value"
                      :style="{
                        color: status.data.color,
                        backgroundColor: 'white',
                        fontWeight: 'bold'
                      }"
                    >
                      {{ status.text }}
                    </b-form-select-option>
                  </b-form-select>
                </div>
              </template>
            </multiple-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      v-if="selectedEoc != null"
      :visible="selectedEoc != null"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="
            w-100
            d-flex
            flex-row
            justify-content-between
            align-items-center
          "
        >
          <h5 class="modal-title">{{ diagnosisCodesFormTitle }}</h5>
          <button
            type="button"
            aria-label="Close"
            class="close text-white"
            style="opacity: 1 !important"
            @click="selectedEoc = null"
          >
            ×
          </button>
        </div>
      </template>
      <template v-slot:modal-footer>
        <div class="text-right mt-1 mr-2">
          <b-button @click="selectedEoc = null" variant="secondary">
            Cancel
          </b-button>
          <b-button @click="saveIcdCodes" variant="primary" class="ml-2">
            Save
          </b-button>
        </div>
      </template>
      <b-form-group label="Diagnosis codes">
        <multiple-select
          v-model="selectedIcdCodes"
          :options="icdCodes"
          :search="(search, loading) => fetchIcdCodeOptions(search, loading)"
          :onDemand="true"
          :allowDuplicates="true"
          placeholder="Search for ICD Codes"
          :disableCache="true"
          :disableFilter="true"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import scheduleEventServer from "../../server/scheduleEvent";
import roomServer from "../../server/scheduleEventRoom";
import userServer from "../../server/user";
import eocServer from "../../server/eoc";
import serviceServer from "../../server/service";
import icdCodeServer from "../../server/icdCode";
import diagnosisCodeServer from "../../server/diagnosisCode";
import locationServer from "../../server/location";

import GroupSessionTypePicker from "./GroupSessionTypePicker.vue";
import { lightenDarkenColor, debounce } from "../../helpers/general";
import {
  validateDate,
  validateNumberLessEqualThan,
  validateNumberNotNegative,
  validateNumberPositive,
  validateRequired
} from "../../helpers/validation";
import RecurrenceControl from "../controls/RecurrenceControl.vue";
import DateTimeDurationPicker from "../controls/DateTimeDurationPicker.vue";
import MultipleSelect from "../controls/MultipleSelect.vue";
import MultipleSelectUnitRate from "../controls/MultipleSelectUnitRate.vue";
import PatientBatchPicker from "../controls/PatientBatchPicker";

import {
  formatDateOnlyMysql,
  formatDatePretty,
  formatDateTimeSql,
  formatEndDateSql
} from "../../../../Breeze.Frontend/src/helpers/date";

export default {
  name: "GroupSessionAddEditModal",
  components: {
    GroupSessionTypePicker,
    RecurrenceControl,
    DateTimeDurationPicker,
    MultipleSelect,
    MultipleSelectUnitRate,
    PatientBatchPicker
  },
  props: ["groupId", "scheduleEventDefaultData", "locationId", "providerId"],
  data() {
    return {
      submit: null,
      form: {
        date: null,
        dateTimeDuration: null,
        services: [],
        providerId: null,
        participants: {},
        eocs: [],
        facilitators: [],
        icdCodes: {},
        status: {},
        topicId: null,
        details: "",
        scheduleZoomMeeting: false,
        scheduleTeamsMeeting: false,
        teamsMeetingUrl: null,
        scheduleGoogleMeetMeeting: false,
        isRecurrent: false,
        recurrence: {
          frequencyNumber: 1,
          frequencyUnit: "Days",
          monthlyRepeat: null,
          endType: "ByOccurrences",
          endDate: null,
          occurrences: 10,
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false
        },
        placeOfService: null,
        room: null
      },
      formErrors: {
        dateTimeDuration: [],
        services: [],
        providerId: [],
        eocs: [],
        facilitators: [],
        topicId: [],
        recurrence: {
          ends: [],
          weekDay: []
        },
        placeOfService: []
      },
      users: [],
      eocs: [],
      services: [],
      icdCodes: [],
      diagnosisCodes: {},
      selectedEoc: null,
      selectedIcdCodes: [],
      servicesContainerHeight: 0,
      rooms: [],
      scheduleEvent: null,
      errorMessage: null,
      noCached: false,
      servicesAll: [],
      usersAll: [],
      location: null,
      servicesEmpty: false,
      lastTimeout: null
    };
  },
  async mounted() {
    this.computeSevicesContainerHeight();
    this.fetchEocs();
    this.fetchRooms();
    await this.getUsers();
    await this.fetchServices();
    await this.fetchScheduleEventStatuses();
    await this.fetchPlacesOfService();
    let form = { ...this.form };
    let icdCodes = { ...this.form.icdCodes };
    this.user = this.scheduleEventDefaultData?.providerId ?? null;
    if (this.locationId) {
      this.location = await locationServer.get(this.locationId);
    }
    if (this.groupId) {
      const group = await scheduleEventServer.get(this.groupId);
      const srv = this.splitServicesByBillingCodes(group);

      if (group?.location?.id) {
        this.location = await locationServer.get(group?.location?.id);
      }

      icdCodes = group.episodesOfCare.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.episodeOfCare.id]: curr.icdCodes.map(u => ({
            value: u.id,
            label: `${u.code} - ${u.description}`,
            data: u
          }))
        };
      }, {});
      this.scheduleEvent = group;

      form = {
        topicId: group.topic ? group.topic.id : null,
        details: group.topicDetails,
        dateTimeDuration: {
          date: this.formatDateOnlyMysql(group.startDateTime),
          time: group.allDay
            ? "00:00:00"
            : this.formatTimeOnlyMysql(group.startDateTime),
          duration: group.allDay
            ? null
            : Math.round(
                moment
                  .duration(
                    moment(group.endDateTime).diff(moment(group.startDateTime))
                  )
                  .asMinutes()
              ),
          allDay: !!group.allDay
        },
        providerId: group.users[0] ? group.users[0].id : "",
        facilitators: group.facilitators.map(u => ({
          ...u,
          label: this.formatUser(u.user),
          value: u.user.id
        })),
        scheduleZoomMeeting: !!group.zoomMeetingUrl,
        scheduleTeamsMeeting: !!group.teamsMeetingUrl,
        scheduleGoogleMeetMeeting: !!group.googleMeetMeetingUrl,
        services: srv.map(service => ({
          value: service.service.id,
          label: `${
            service.service.billingCodes[0]
              ? service.service.billingCodes[0].cptCode
              : ""
          } - ${service.service.name}`,
          data: service,
          costPerUnit: service.costPerUnit,
          numberOfUnits: service.numberOfUnits,
          billingCodes: service.scheduleEventServiceBillingCodes.map(bc => ({
            ...bc,
            numberOfUnits: bc.numberOfUnits
              ? bc.numberOfUnits
              : service.numberOfUnits
          }))
        })),
        eocs: group.episodesOfCare.map(participant => ({
          value: participant.episodeOfCare.id,
          label:
            participant.episodeOfCare.mrnEocn +
            " " +
            participant.episodeOfCare.patient.firstName +
            " " +
            participant.episodeOfCare.patient.lastName,
          data: { ...participant.episodeOfCare, participantId: participant.id }
        })),
        participants: group.episodesOfCare.reduce((acc, curr) => {
          return {
            ...acc,
            [curr.episodeOfCare.id]: curr.id
          };
        }, {}),
        icdCodes,
        status: group.episodesOfCare.reduce((acc, curr) => {
          return {
            ...acc,
            [curr.episodeOfCare.id]: curr.scheduleEventStatus.id
          };
        }, {}),
        isRecurrent: !!group.recurrence,
        recurrence: group.recurrence
          ? {
              id: group.recurrence?.id,
              frequencyNumber: group.recurrence?.frequencyNumber,
              frequencyUnit: group.recurrence?.frequencyUnit,
              endType: group.recurrence?.endType,
              endDate: group.recurrence?.endDate
                ? this.formatDateTimeMysql(group.recurrence.endDate)
                : "",
              occurrences: group.recurrence?.occurrences,
              monthlyRepeat: group.recurrence?.monthlyRepeat,
              sunday: group.recurrence?.sunday,
              monday: group.recurrence?.monday,
              tuesday: group.recurrence?.tuesday,
              wednesday: group.recurrence?.wednesday,
              thursday: group.recurrence?.thursday,
              friday: group.recurrence?.friday,
              saturday: group.recurrence?.saturday
            }
          : {
              frequencyNumber: 1,
              frequencyUnit: "Days",
              monthlyRepeat: null,
              endType: "ByOccurrences",
              endDate: null,
              occurrences: 10,
              sunday: false,
              monday: false,
              tuesday: false,
              wednesday: false,
              thursday: false,
              friday: false,
              saturday: false
            },
        placeOfService: group.placeOfService
          ? {
              value: group.placeOfService.id,
              label: `${group.placeOfService.code}-${group.placeOfService.name}`,
              data: group.placeOfService
            }
          : null,
        room: group.room
          ? {
              value: group.room.id,
              label: group.room.name,
              data: group.room
            }
          : null
      };
    } else if (this.scheduleEventDefaultData) {
      const scheduleEvent = this.scheduleEventDefaultData;
      form.dateTimeDuration = {
        date: this.formatDateOnlyMysql(scheduleEvent.startDateTime),
        time: this.formatTimeOnlyMysql(scheduleEvent.startDateTime),
        duration: moment
          .duration(
            moment(scheduleEvent.endDateTime).diff(
              moment(scheduleEvent.startDateTime)
            )
          )
          .asMinutes(),
        allDay: false
      };
      form.placeOfService = this.placesOfServiceOptions.find(
        s => s.data.code == this.defaultPlaceOfServiceCode
      );

      const currentLocation = this.selectedLocation ?? this.location;
      if (currentLocation.defaultServiceId) {
        const service = this.services.find(
          x => x.value == currentLocation.defaultServiceId
        );
        if (service) {
          form.services.push(service);
        }
      }
      form.providerId = this.scheduleEventDefaultData.providerId;
    } else {
      form.dateTimeDuration = {
        date: null,
        time: null,
        duration: 60,
        allDay: false
      };

      form.placeOfService = this.placesOfServiceOptions.find(
        s => s.data.code == this.defaultPlaceOfServiceCode
      );
      form.providerId = this.providerId;

      const currentLocation = this.selectedLocation ?? this.location;
      if (currentLocation.defaultServiceId) {
        const service = this.services.find(
          x => x.value == currentLocation.defaultServiceId
        );
        if (service) {
          form.services.push(service);
        }
      }
    }
    this.form.icdCodes = { ...icdCodes };
    this.form = { ...form };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", [
      "fetchScheduleEventStatuses",
      "fetchPlacesOfService"
    ]),
    removeDiagnosisCode(value) {
      this.$delete(this.form.icdCodes, value);
      this.$delete(this.diagnosisCodes, value);
    },
    addPatients(selectedValues) {
      let patients = selectedValues.map(p => ({
        value: p.id,
        label: p.mrnEocn + " " + p.patient.firstName + " " + p.patient.lastName,
        data: p
      }));
      patients = patients.filter(
        ar => !this.form.eocs.find(rm => rm.value === ar.value)
      );
      this.form.eocs = this.form.eocs.concat(patients);
    },
    idInEocs(id) {
      if (this.eocs.map(e => e.value).includes(id)) {
        return true;
      }
      return false;
    },
    onHidden() {},
    validateGroupSubmit() {
      let withoutErrors = true;

      this.formErrors.providerId = [];
      if (this.requireProviderInEvents) {
        if (!validateRequired(this.form.providerId)) {
          this.formErrors.providerId.push("This field is required");
          withoutErrors = false;
        }
      }
      this.formErrors.eocs = [];
      this.formErrors.topicId = [];
      if (!validateRequired(this.form.topicId)) {
        this.formErrors.topicId.push("Must select a group type");
        withoutErrors = false;
      }

      this.formErrors.placeOfService = [];
      if (!validateRequired(this.form.placeOfService)) {
        this.formErrors.placeOfService.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.services = [];
      if (this.form.services.length == 0) {
        this.formErrors.services.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.dateTimeDuration = [];
      if (
        !validateRequired(this.form.dateTimeDuration.date) ||
        (!this.form.dateTimeDuration.allDay &&
          (!validateRequired(this.form.dateTimeDuration.time) ||
            !validateRequired(this.form.dateTimeDuration.duration)))
      ) {
        this.formErrors.dateTimeDuration.push("This field is required");
        withoutErrors = false;
      } else if (!validateDate(this.form.dateTimeDuration.date)) {
        this.formErrors.dateTimeDuration.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.datesNonWorking(
          null,
          new Date(this.form.dateTimeDuration.date + " 00:00:00")
        ) ||
        (!this.form.dateTimeDuration.allDay &&
          ((this.selectedLocation &&
            this.form.dateTimeDuration.time <
              this.selectedLocation.operationStarts) ||
            (this.selectedLocation &&
              this.form.dateTimeDuration.time >
                this.selectedLocation.operationEnds) ||
            (!this.selectedLocation &&
              this.form.dateTimeDuration.time <
                this.location.operationStarts) ||
            (!this.selectedLocation &&
              this.form.dateTimeDuration.time > this.location.operationEnds)))
      ) {
        this.formErrors.dateTimeDuration.push(
          "Events cannot be created outside hours of operation"
        );
        withoutErrors = false;
      } else if (
        !this.form.dateTimeDuration.allDay &&
        (!validateNumberNotNegative(this.form.dateTimeDuration.duration) ||
          !validateNumberPositive(this.form.dateTimeDuration.duration))
      ) {
        this.formErrors.dateTimeDuration.push(
          "Duration cannot be a negative number!"
        );
        withoutErrors = false;
      } else if (
        !this.form.dateTimeDuration.allDay &&
        !validateNumberLessEqualThan(this.form.dateTimeDuration.duration, 1440)
      ) {
        this.formErrors.dateTimeDuration.push("Maximum value of 1440 minutes");
        withoutErrors = false;
      }

      for (let i = 0; i < this.form.services.length; i++) {
        const service = this.form.services[i];
        for (let k = 0; k < service.billingCodes.length; k++) {
          if (
            !validateNumberNotNegative(service.billingCodes[k].costPerUnit) ||
            !validateNumberPositive(service.billingCodes[k].numberOfUnits)
          ) {
            this.formErrors.services.push(
              "This field must have a positive value"
            );
            withoutErrors = false;
          }
        }
      }

      this.formErrors.recurrence.ends = [];
      if (
        this.form.recurrence.endType == "ByDate" &&
        !validateRequired(this.form.recurrence.endDate)
      ) {
        this.formErrors.recurrence.ends.push(
          "You must specify a date to end the recurrence"
        );
        withoutErrors = false;
      } else if (
        this.form.recurrence.endType === "ByDate" &&
        !validateDate(formatDateOnlyMysql(this.form.recurrence.endDate))
      ) {
        this.formErrors.recurrence.ends.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.form.recurrence.endType == "ByDate" &&
        this.form.recurrence.endDate < this.form.dateTimeDuration.date
      ) {
        this.formErrors.recurrence.ends.push(
          "Recurrence end date must be greater than start date"
        );
        withoutErrors = false;
      }
      if (
        this.user &&
        this.form.services.length > 0 &&
        this.user.data.services.length < this.servicesAll.length
      ) {
        let userServicesId = this.user.data.services;
        let servicesNoProviders = this.form.services.filter(
          s => !userServicesId.includes(s.value)
        );
        if (servicesNoProviders.length > 0) {
          this.formErrors.services.push(
            "Selected provider does not provide services: " +
              servicesNoProviders.map(s => s.label).join(", ")
          );
          withoutErrors = false;
        }
      }
      return withoutErrors;
    },
    async submitGroup(modalEvent, modificationType = "ModifyRecurrence") {
      modalEvent.preventDefault();
      const result = this.validateGroupSubmit();

      let checkData = {
        users: this.form.providerId ? [this.form.providerId] : [],
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime
      };
      if (this.groupId) checkData.id = this.groupId;
      if (!result) return;
      const noDoubleBooking = await this.checkForDoubleBookedEvent(checkData);
      let doubleBookingAlert = {
        data: {
          value: true
        }
      };
      if (noDoubleBooking.doubleBooked) {
        try {
          doubleBookingAlert = await this.$dialog.confirm(
            {
              title: "This provider already has an appointment at this time."
            },
            {
              view: "multiple-actions",
              cancelText: "Keep editing",
              options: [{ value: true, label: "Save" }]
            }
          );
        } catch (error) {
          return;
        }
      }

      if (!doubleBookingAlert.data.value) {
        return;
      }
      let response = {};
      const groupedServices = this.groupServices();

      const formData = {
        type: "GroupSession",
        topicId: this.form.topicId,
        topicDetails: this.form.details,
        allDay: this.form.dateTimeDuration.allDay,
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime,
        users: this.form.providerId ? [this.form.providerId] : [],
        facilitators: this.form.facilitators.map(u => ({
          userId: u.value
        })),
        scheduleZoomMeeting: this.form.scheduleZoomMeeting,
        scheduleTeamsMeeting: this.form.scheduleTeamsMeeting,
        scheduleGoogleMeetMeeting: this.form.scheduleGoogleMeetMeeting,
        modification: modificationType,
        episodesOfCare: this.form.eocs.map(c => ({
          id: this.form.participants[c.value],
          episodeOfCareId: c.value,
          icdCodeIds: this.form.icdCodes[c.value].map(ic => ic.value),
          statusId: this.form.status[c.value]
        })),
        services: groupedServices.map(service => ({
          serviceId: service.serviceId,
          costPerUnit:
            service.billingCodes.length && service.billingCodes[0]?.costPerUnit
              ? parseFloat(service.billingCodes[0].costPerUnit)
              : 0,
          numberOfUnits:
            service.billingCodes.length &&
            service.billingCodes[0]?.numberOfUnits
              ? parseFloat(service.billingCodes[0]?.numberOfUnits)
              : 0,
          billingCodes: service.billingCodes.map(bc => ({
            ...bc,
            costPerUnit: bc.costPerUnit != "" || null ? bc.costPerUnit : 0,
            numberOfUnits:
              bc.numberOfUnits != "" || null ? bc.numberOfUnits : 1,
            scheduleEventId: this.scheduleEventId,
            serviceId: service.serviceId
          })),
          color: service.color != null ? service.color : null
        })),
        ...(this.form.isRecurrent && {
          recurrence: {
            frequencyNumber: this.form.recurrence.frequencyNumber,
            frequencyUnit: this.form.recurrence.frequencyUnit,
            endType: this.form.recurrence.endType,
            ...(this.form.recurrence.endType == "ByDate" && {
              endDate: validateDate(this.form.recurrence.endDate)
                ? formatEndDateSql(this.form.recurrence.endDate)
                : formatDateTimeSql(this.form.recurrence.endDate)
            }),
            ...(this.form.recurrence.endType == "ByOccurrences" && {
              occurrences: this.form.recurrence.occurrences
            }),
            ...(this.form.recurrence.frequencyUnit == "Months" && {
              monthlyRepeat: this.form.recurrence.monthlyRepeat
            }),
            ...(this.form.recurrence.frequencyUnit == "Weeks" && {
              sunday: this.form.recurrence.sunday,
              monday: this.form.recurrence.monday,
              tuesday: this.form.recurrence.tuesday,
              wednesday: this.form.recurrence.wednesday,
              thursday: this.form.recurrence.thursday,
              friday: this.form.recurrence.friday,
              saturday: this.form.recurrence.saturday
            })
          }
        }),
        placeOfServiceId: this.form.placeOfService
          ? this.form.placeOfService.value
          : null,
        roomId: this.form.room ? this.form.room.value : null,
        locationId: this.location?.id
      };

      if (this.groupId) {
        const dialog = await this.$dialog.alert(
          {
            title: "Do you want to notify group participants?"
          },
          {
            view: "multiple-actions",
            options: [
              { value: true, label: "Yes" },
              { value: false, label: "No" }
            ]
          }
        );
        formData.resend = dialog.data.value;
        try {
          response = await scheduleEventServer.update(this.groupId, formData);
        } catch (error) {
          if (error.response.data.date) {
            this.errorMessage = `${
              error.response.data.message
            } ${formatDatePretty(error.response.data.date)}`;
          } else {
            this.errorMessage =
              error.response.data.errorCode === "tooMuchOcurrences"
                ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                : error.response.data.message;
          }
          return;
        }
      } else {
        try {
          response = await scheduleEventServer.create(formData);
        } catch (error) {
          if (error.response.data.date) {
            this.errorMessage = `${
              error.response.data.message
            } ${formatDatePretty(error.response.data.date)}`;
          } else {
            this.errorMessage =
              error.response.data.errorCode === "tooMuchOcurrences"
                ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                : error.response.data.message;
          }
          return;
        }
      }
      if (formData.scheduleZoomMeeting && !response.zoomMeetingUrl) {
        this.$dialog.alert(
          {
            title: `The event was ${
              this.groupId ? `updated` : `created`
            } successfully. The user doesn't have an active account on Zoom. For this reason, the zoom meeting was not attached.`
          },
          {
            view: "info"
          }
        );
      }
      if (formData.scheduleTeamsMeeting && !response.teamsMeetingUrl) {
        this.$dialog.alert(
          {
            title: `The event was ${
              this.groupId ? `updated` : `created`
            } successfully but the teams meeting was not attached.`
          },
          {
            view: "info"
          }
        );
      }
      if (formData.scheduleGoogleMeetMeeting && !response.googleMeetMeetingUrl) {
        this.$dialog.alert(
          {
            title: `The event was ${
              this.groupId ? `updated` : `created`
            } successfully, but the google meet meeting was not attached.`
          },
          {
            view: "info"
          }
        );
      }
      this.showAlertSuccess();
      this.$emit("onModify");
    },
    async getUsers() {
      const users = await userServer.getUserServices();
      this.usersAll = users
        .filter(user => user.providerInfo)
        .map(user => ({
          value: user.id,
          label: this.formatUser(user),
          data: user
        }));
      this.users = this.usersAll;
    },
    async fetchRooms() {
      const rooms = await roomServer.query();
      this.rooms = rooms.map(room => ({
        value: room.id,
        label: room.name,
        data: room
      }));
    },
    async fetchEocs() {
      const eocs = await eocServer.queryList(
        {
          searchPhrase: "",
          eocStatus: ["Active", "Pending"],
          locations: this.locationId ? [this.locationId] : [],
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.eocs = eocs.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label:
          eoc.mrnEocn +
          " " +
          eoc.patient.firstName +
          " " +
          eoc.patient.lastName,
        data: eoc
      }));
    },
    fetchEocsOptionsPicker(search, loading) {
      if (!search || search.length == 0 || search.length >= 3) {
        loading(true);
        this.search(loading, search, this);
      }
    },
    search: debounce((loading, search, vm) => {
      loading(true);
      eocServer
        .queryList(
          {
            searchPhrase: search,
            eocStatus:
              !search || search.length == 0 ? ["Active", "Pending"] : [],
            locations: vm.locationId ? [vm.locationId] : [],
            orderBy: "status",
            page: 1,
            pageSize: 50
          },
          {
            "Hide-Loading": true
          }
        )
        .then(res => {
          vm.eocs = res.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label:
              eoc.mrnEocn +
              " " +
              eoc.patient.firstName +
              " " +
              eoc.patient.lastName,
            data: eoc
          }));

          loading(false);
        });
    }, 350),
    async fetchServices() {
      const services = await serviceServer.query({
        type: "Service"
      });
      this.servicesAll = services.map(service => ({
        value: service.id,
        label: `${
          service.billingCodes[0] ? service.billingCodes[0].cptCode + " - " : ""
        } ${service.name}`,
        data: service,
        costPerUnit: service.costPerUnit,
        numberOfUnits: 1,
        billingCodes: service.billingCodes.map(bc => ({
          ...bc,
          mod1: bc.mod1 ? bc.mod1 : "",
          mod2: bc.mod2 ? bc.mod2 : "",
          mod3: bc.mod3 ? bc.mod3 : "",
          mod4: bc.mod4 ? bc.mod4 : ""
        })),
        color: service.color
      }));
      this.services = this.servicesAll;
    },
    async fetchIcdCodeOptions(search, loading) {
      if (this.IcdCodesTimeout) {
        clearTimeout(this.IcdCodesTimeout);
        this.IcdCodesTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.icdCodes = [];
      }

      this.IcdCodesTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const icdCodes = await icdCodeServer.query(
            { searchPhrase: search },
            {
              "Hide-Loading": true
            }
          );
          this.icdCodes = icdCodes.map(icdCode => ({
            value: icdCode.id,
            label: `${icdCode.code} - ${icdCode.description}`,
            data: icdCode
          }));
        } else this.icdCodes = [];
        loading(false);
      }, 1000);
    },
    async fetchEocDiagnosisCodes(eocId) {
      if (!this.diagnosisCodes[eocId]) {
        this.diagnosisCodes[eocId] = await diagnosisCodeServer.query(
          false,
          eocId
        );
      }
    },
    getEocActiveDiagnosisCodes(eocId) {
      const date =
        this.form.dateTimeDuration.date != null
          ? moment(this.form.dateTimeDuration.date, "YYYY-MM-DD").toDate()
          : null;
      return this.diagnosisCodes[eocId]
        ? this.diagnosisCodes[eocId]
            .filter(d => {
              const activeFrom =
                d.activeFrom && moment(d.activeFrom, "YYYY-MM-DD").toDate();
              const resolvedBy =
                d.resolvedBy && moment(d.resolvedBy, "YYYY-MM-DD").toDate();
              return (
                (d.status.name == "Active" || d.status.name == "Resolved") &&
                (activeFrom == null || activeFrom <= date || date == null) &&
                (resolvedBy == null || date <= resolvedBy || date == null)
              );
            })
            .map(d => ({
              value: d.icdCode.id,
              label: `${d.icdCode.code} - ${d.icdCode.description}`,
              data: d.icdCode
            }))
        : [];
    },
    statusLabelColor(statusId) {
      if (this.statuses.length == 0 || !statusId) {
        return "";
      }

      return this.statuses.find(u => u.value == statusId).data.color;
    },
    statusBackgroundColor(statusId) {
      if (this.statuses.length == 0 || !statusId) {
        return "";
      }

      return lightenDarkenColor(
        this.statuses.find(u => u.value == statusId).data.color,
        85
      );
    },
    editIcdCodes(eoc) {
      this.selectedEoc = eoc;
      this.selectedIcdCodes = [...this.form.icdCodes[this.selectedEoc.id]];
    },
    saveIcdCodes() {
      this.form.icdCodes[this.selectedEoc.id] = [...this.selectedIcdCodes];
      this.selectedEoc = null;
      this.selectedIcdCodes = [];
    },
    computeSevicesContainerHeight() {
      setTimeout(() => {
        const servicesContainerHeight = this.$refs.servicesContainer
          .clientHeight;
        this.servicesContainerHeight = servicesContainerHeight;
      }, 0);
    },
    statusVariant(status) {
      switch (status) {
        case "Pending":
          return "warning";
        case "Active":
          return "success";
        case "Inactive":
          return "danger";
        default:
          return "";
      }
    },
    statusText(status) {
      return status.substring(0, 1);
    },
    async checkForDoubleBookedEvent(data) {
      return await scheduleEventServer
        .checkForDoubleBookedEvent(data)
        .then(result => {
          return result;
        });
    },
    splitServicesByBillingCodes(scheduleEvent) {
      let services = scheduleEvent.services || [];
      let result = [];

      services.forEach(service => {
        let billingCodes =
          service.service.scheduleEventServiceBillingCodes || [];

        billingCodes.forEach(billingCode => {
          let byBillingCode = {
            ...service,
            scheduleEventServiceBillingCodes: [billingCode]
          };

          result.push(byBillingCode);
        });
      });

      return result;
    },
    groupServices() {
      const result = [];
      const groups = {};

      this.form.services.forEach(item => {
        const serviceId = item.value;
        if (!groups[serviceId]) {
          groups[serviceId] = [];
          result.push({ serviceId, services: groups[serviceId] });
        }
        groups[serviceId].push(item);
      });

      return result.map(item => ({
        ...item,
        data: item.services[0],
        billingCodes: [].concat(
          ...item.services.map(service => service.billingCodes)
        )
      }));
    }
  },
  computed: {
    ...mapGetters("layout", [
      "datesNonWorking",
      "getCurrentAdjustedDateTime",
      "hasCurrentUserPermissionAccess"
    ]),
    ...mapGetters("client", ["selectedLocation"]),
    ...mapState("client", {
      statuses: state =>
        state.scheduleEventStatuses.map(status => ({
          value: status.id,
          text: status.name,
          data: status
        })),
      placesOfServiceOptions: state =>
        state.placesOfService.map(placeOfService => ({
          value: placeOfService.id,
          label: `${placeOfService.code}-${placeOfService.name}`,
          data: placeOfService
        }))
    }),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      isZoomEnabled: state => state.systemSetting.isZoomEnabled,
      isTeamsEnabled: state => state.systemSetting.isTeamsEnabled,
      isGoogleMeetEnabled: state => state.systemSetting.isGoogleMeetEnabled,
      teleHealtAutomaticModifier: state =>
        state.systemSetting.enableTelehealthSetMod,
      defaultPlaceOfServiceCode: state =>
        state.systemSetting.defaultPlaceOfServiceCode,
      requireProviderInEvents: state =>
        state.systemSetting.requireProviderInEvents
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    user: {
      get() {
        return this.form.providerId
          ? this.users.find(user => user.value == this.form.providerId)
          : null;
      },
      set(newValue) {
        this.form.providerId = newValue ? newValue.value : null;
      }
    },
    startDateTime() {
      if (!this.form.dateTimeDuration) {
        return null;
      }
      if (!this.form.dateTimeDuration.date) {
        return null;
      }
      if (
        !this.form.dateTimeDuration.time &&
        !this.form.dateTimeDuration.allDay
      ) {
        return null;
      }

      const dateTime = `${this.form.dateTimeDuration.date} ${
        this.form.dateTimeDuration.allDay
          ? "00:00:00"
          : this.form.dateTimeDuration.time
      }`;

      return formatDateTimeSql(dateTime);
    },
    endDateTime() {
      if (!this.form.dateTimeDuration) {
        return null;
      }
      if (!this.form.dateTimeDuration.date) {
        return null;
      }
      if (
        !this.form.dateTimeDuration.time ||
        this.form.dateTimeDuration.allDay
      ) {
        return null;
      }

      const dateTime = moment(this.startDateTime).add(
        this.form.dateTimeDuration.duration,
        "minutes"
      );

      return formatDateTimeSql(dateTime);
    },
    belongsToRecurrence() {
      return !!this.form.recurrence?.id;
    },
    formTitle() {
      if (this.groupId) {
        return this.belongsToRecurrence
          ? "Edit recurrent group session"
          : "Edit group session";
      } else {
        return "Add Group Session";
      }
    },
    eventIsInPast() {
      return (
        !this.startDateTime ||
        this.formatDateTimeMysql(this.startDateTime) <=
          this.formatDateTimeMysql(this.getCurrentAdjustedDateTime())
      );
    },
    diagnosisCodesFormTitle() {
      return `Diagnosis Codes - ${this.formatEocObj(this.selectedEoc)}`;
    },
    applyTeleHealtAutomaticModifier() {
      return (
        this.teleHealtAutomaticModifier &&
        this.form.placeOfService &&
        this.form.placeOfService.data.code === "02"
      );
    }
  },
  watch: {
    "form.eocs": async function(newValue, oldValue) {
      const addedValues = newValue.filter(p => !oldValue.includes(p));
      if (addedValues) {
        for (const value of addedValues) {
          await this.fetchEocDiagnosisCodes(value.value);
          const icdCodes = this.getEocActiveDiagnosisCodes(value.value);
          const status = this.statuses.find(s => s.data.code == "show").value;
          if (!this.form.icdCodes[value.value]) {
            this.$set(this.form.icdCodes, value.value, icdCodes);
          }
          if (!this.scheduleEvent) {
            this.$set(this.form.status, value.value, status);
          } else if (
            !this.scheduleEvent.episodesOfCare.find(
              eoc => eoc.id === value.data.participantId
            )
          ) {
            this.$set(this.form.status, value.value, status);
          }
        }
      }

      const removed = oldValue.filter(p => !newValue.includes(p)).shift();
      if (removed) {
        const participant = this.form.participants[removed.value];
        if (participant) {
          delete this.form.participants[removed.value];
        }
      }
    },
    "form.dateTimeDuration": async function(newValue, oldValue) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(async () => {
        if (
          newValue?.date &&
          oldValue?.date &&
          newValue.date != oldValue.date
        ) {
          this.form.eocs.forEach(eoc => {
            this.form.icdCodes[eoc.value] = this.getEocActiveDiagnosisCodes(
              eoc.value
            );
          });

          this.formErrors.dateTimeDuration = [];
          if (!validateDate(newValue.date)) {
            this.formErrors.dateTimeDuration.push("Invalid date");
          } else if (
            this.datesNonWorking(null, new Date(newValue.date + " 00:00:00"))
          ) {
            this.formErrors.dateTimeDuration.push(
              "Events cannot be created outside hours of operation"
            );
          }
        }
        if (
          newValue.date != null &&
          newValue.time != null &&
          !newValue.allDay &&
          (this.groupId == null ||
            (oldValue != null && oldValue.duration != newValue.duration)) &&
          this.form.services.length > 0
        ) {
          let serviceIds = [];
          for (let service of this.form.services) {
            serviceIds.push(service.value);
          }

          let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
            this.startDateTime,
            this.endDateTime,
            "Show",
            serviceIds
          );

          if (Object.keys(servicePerUnit).length > 0) {
            this.form.services.forEach(service => {
              service.billingCodes.forEach(bc => {
                bc.numberOfUnits = servicePerUnit[service.value];
              });
            });
          }
        }
      }, 1500);
    },
    "form.services": {
      handler: async function(value, oldValue) {
        this.computeSevicesContainerHeight();
        if (value && value.length > 0) {
          let servicesId = this.form.services.map(s => s.value);
          this.users = this.usersAll.filter(u =>
            servicesId.every(
              s =>
                u.data.services?.includes(s) ||
                u.data.services.length == this.servicesAll.length
            )
          );
        } else {
          this.users = this.usersAll;
        }
        if (
          value.length > oldValue.length &&
          this.startDateTime != null &&
          this.endDateTime != null
        ) {
          let serviceIds = [];
          for (let service of value) {
            serviceIds.push(service.value);
          }
          let serviceIdsOld = [];
          for (let service of oldValue) {
            serviceIdsOld.push(service.value);
          }

          let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
            this.startDateTime,
            this.endDateTime,
            "Show",
            serviceIds
          );

          if (Object.keys(servicePerUnit).length > 0) {
            value.forEach(service => {
              let numberOfUnits = servicePerUnit[service.value];
              service.billingCodes.forEach(bc => {
                bc.numberOfUnits =
                  this.groupId != null &&
                  serviceIdsOld.length == 0 &&
                  !this.servicesEmpty
                    ? bc.numberOfUnits
                    : this.groupId != null &&
                      !this.servicesEmpty &&
                      serviceIdsOld.includes(service.value)
                    ? bc.numberOfUnits
                    : numberOfUnits;
              });
            });
          }
        }
        if (oldValue.length > value.length && value.length == 0) {
          this.servicesEmpty = true;
        } else {
          this.servicesEmpty = false;
        }
      },
      deep: true
    },
    selectedIcdCodes: async function(value) {
      if (value && value.length > 0) {
        const diagnosisCodes = await diagnosisCodeServer.query(
          false,
          this.selectedEoc.id
        );
        this.selectedIcdCodes.forEach(item => {
          const diagnosisCodeElement = diagnosisCodes.find(
            x => x.icdCode.id == item.value
          );
          if (
            diagnosisCodeElement != null &&
            item.label.substring(0, 5) != "<span"
          ) {
            const newLabel = diagnosisCodeElement.category
              ? `<span class="badge text-white mr-1 p-1 badge-primary">${diagnosisCodeElement.category[0]}</span> ${item.label}`
              : "";
            item.label = newLabel;
          }
        });
      }
    },
    "form.icdCodes": async function() {
      if (Object.entries(this.form.icdCodes).length > 0) {
        Object.entries(this.form.icdCodes).forEach(async ([key, value]) => {
          const diagnosisCodes = await diagnosisCodeServer.query(false, key);
          if (diagnosisCodes.length > 0) {
            value.forEach(item => {
              const diagnosisCodeElement = diagnosisCodes.find(
                x => x.icdCode.id == item.value
              );
              if (
                diagnosisCodeElement != null &&
                item.data.code.substring(0, 5) != "<span"
              ) {
                const newCode = diagnosisCodeElement.category
                  ? `<span class="badge text-white mr-1 p-1 badge-primary">${diagnosisCodeElement.category[0]}</span> ${item.data.code}`
                  : item.data.code;
                item.data.code = newCode;
                item.category = diagnosisCodeElement.category;
                value.sort((a, b) => {
                  return a.category < b.category
                    ? -1
                    : a.category > b.category
                    ? 1
                    : 0;
                });
              }
            });
          }
        });
      }
    },
    applyTeleHealtAutomaticModifier: function(newVal) {
      if (newVal === true) {
        if (this.form.services.length > 0) {
          this.form.services.forEach(service => {
            if (service.billingCodes.length > 0) {
              service.billingCodes.forEach(bc => {
                if (bc.mod1 === "") {
                  bc.mod1 = "95";
                }
              });
            }
          });
        }
        if (this.services.length > 0) {
          this.services.forEach(service => {
            if (service.billingCodes && service.billingCodes.length > 0) {
              service.billingCodes.forEach(bc => {
                bc.mod1 === "" ? (bc.mod1 = "95") : null;
              });
            } else {
              this.$set(service, "billingCodes", [
                {
                  ...service.billingCodes,
                  mod1: "95",
                  costPerUnit: 0,
                  numberOfUnits: 1,
                  mod2: "",
                  mod3: "",
                  mod4: ""
                }
              ]);
            }
          });
        }
      }
    },
    user: {
      handler(value) {
        if (value) {
          if (value.data.services.length == this.servicesAll.length) {
            this.services = this.servicesAll;
          } else {
            this.services = this.servicesAll.filter(s =>
              value.data.services?.includes(s.value)
            );
          }
          this.noCached = true;
        } else {
          this.services = this.servicesAll;
          this.noCached = false;
          if (this.form.services.length > 0) {
            this.formErrors.services = [];
          }
        }
      },
      deep: true
    }
  }
};
</script>
<style lang="scss" scoped>
.multiple-patients {
  position: absolute;
  right: 8px;
  top: -5px;
}

::v-deep .vs__dropdown-menu {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  li {
    white-space: normal;
  }
}
</style>
